import classNames from 'classnames'
import { useRouter } from 'next/router'
import React, { ReactElement, TouchEvent, WheelEvent, useEffect, useMemo, useRef, useState } from 'react'

import { saveProjectInnerRoute } from '../../../../api/projects'
import ChordEditIcon from '../../../../assets/icons/chord-edit.svg'
import ExternalLinkIcon from '../../../../assets/icons/external-link.svg'
import PlayIcon from '../../../../assets/icons/play.svg'
import { useInternationalization } from '../../../../context/InternationalizationContext'
import useLimitedRequest from '../../../../hooks/useLimitedRequest'
import useSizes from '../../../../hooks/useSizes'
import { chordsToPartTimings } from '../../../../utils/audio/audioUtils'
import { getBarsFromSixteens } from '../../../../utils/audio/midiUtils'
import { removeScaleFromDegree } from '../../../../utils/chord-editor'
import { hjEvent } from '../../../../utils/hotjar'
import { getActivePart, getChordByIdFromProg, getIsTrackMuted } from '../../../../utils/progUtils'
import Routes from '../../../../utils/routes'
import { windowOpen } from '../../../../utils/stringUtils'
import { Chord, defaultLyrics, defaultProjectData, defaultView, Prog } from '../../../../utils/types'
import VolumeControls from '../../../editor/Footer/VolumeControls'
import CircleButton from '../../CircleButton'
import IconMenuButtonLight from '../../IconMenuButtonLight'
import PianoRoll from '../../PianoRoll'
import Tooltip from '../../Tooltip/Tooltip'
import PlayPauseAnimation from './PlayPauseAnimation'
import styles from './Preview.module.scss'

const blogPreviewKeyTooltipId = 'blog-preview-key-tooltip-id'
const blogPreviewBpmTooltipId = 'blog-preview-bpm-tooltip-id'

type Props = {
  playDisabled: boolean
  handlePlay: () => void
  handleReset: () => void
  handleVolumeChange: (v: number) => void
  loading: boolean
  bpm: number
  isPlaying: boolean
  volume: number
  id: string
  previewKey: string
  activeChordId?: number | null
  prog: Prog
  currentPartId: number
  fullPage?: boolean
  playChord?: (ch: Chord) => void
  renderFooter?: (a: () => ReactElement, b: () => ReactElement) => ReactElement
  renderDrumsPreview?: (scale: number) => ReactElement
  repeat: boolean
  handleRepeat: () => void
  playheadPosition: number | null
}

const Preview: React.FC<Props> = ({
  playDisabled,
  loading,
  isPlaying,
  volume,
  repeat,
  playheadPosition,

  handlePlay,
  handleReset,
  handleVolumeChange,
  handleRepeat,
  playChord,

  currentPartId,
  prog,
  id,
  previewKey,
  bpm,
  activeChordId,
  fullPage,
  renderDrumsPreview,
  renderFooter,
}) => {
  const router = useRouter()
  const { isMobile, isSmallDesktop } = useSizes()
  const { text: STRINGS } = useInternationalization()

  const containerRef = useRef<HTMLDivElement>(null)
  const chordsContainerRef = useRef<HTMLDivElement>(null)

  const [localVolume, setLocalVolume] = useState(volume)
  const [showPlay, setShowPlay] = useState(fullPage)
  const [muted, setMuted] = useState(false)

  const [scale, setScale] = useState(1)
  const [scaleTouchStartDistance, setScaleTouchStartDistance] = useState(0)
  const [isManualScrollActive, setIsManualScrollActive] = useState(false)
  const [prevScroll, setPrevScroll] = useState(0)

  const CHORD_WIDTH = fullPage ? 128 : 104
  const activeChord = activeChordId ? getChordByIdFromProg(activeChordId, prog) : null

  const maxScale = 3
  const minScale = 0.37

  const pinchSlew = 450 * 5
  const wheelSlew = -400

  const activePart = useMemo(() => getActivePart(prog, currentPartId) || prog.parts[0], [prog, currentPartId])
  const { drumsDuration } = useMemo(() => chordsToPartTimings(prog), [activePart])
  const contentWidth = useMemo(() => getBarsFromSixteens(drumsDuration) * 128 * scale + 10 * 2, [drumsDuration, scale])

  const playheadPositionInPx = Math.ceil((playheadPosition! - 1) * 128 * scale) + 10

  const windowCenter = useMemo(() => window.innerWidth / 2, [window])

  const handleOpenPreviewInStudio = useLimitedRequest(async () => {
    const page = router.asPath.split('?')[0].replaceAll('/', '')
    const utmParams = fullPage ? '' : `&utm_source=blog&utm_medium=widget&utm_content=${page}`

    const project = await saveProjectInnerRoute(prog, defaultView, defaultLyrics, defaultProjectData)

    hjEvent('event', 'open_in_chordchord')
    windowOpen(`${Routes.STUDIO}/${project._id}?${utmParams}`, '_blank', true)
  }, 'projects')

  useEffect(() => {
    if (!isMobile || playheadPositionInPx < 0 || isManualScrollActive || playheadPosition === null) return

    const scrollTo = (v: number) => {
      if (v === chordsContainerRef.current?.scrollLeft || Math.abs(v - prevScroll) < 10) return

      setPrevScroll(v)

      chordsContainerRef.current?.scrollTo({ left: v, behavior: (v === 0 ? 'instant' : 'smooth') as ScrollBehavior })
    }

    if (playheadPositionInPx < windowCenter) {
      scrollTo(0)
      setPrevScroll(0)
      return
    }
    if (playheadPositionInPx > contentWidth - windowCenter) {
      if ((chordsContainerRef.current?.scrollLeft || contentWidth) < contentWidth - windowCenter) {
        scrollTo(playheadPositionInPx)
      }
      return
    }

    scrollTo(playheadPositionInPx - windowCenter)
  }, [playheadPositionInPx, isManualScrollActive])
  useEffect(() => {
    const timelineWrap: HTMLElement = containerRef.current!

    function listener(e: MouseEvent) {
      if (e.ctrlKey && !e.shiftKey) e.preventDefault()
    }

    timelineWrap.addEventListener('wheel', listener)
    return () => timelineWrap.removeEventListener('wheel', listener)
  }, [])
  useEffect(() => {
    if (isPlaying && showPlay) setShowPlay(false)
  }, [isPlaying])

  const scaleCutoff = (v: number) => Math.max(Math.min(v, maxScale), minScale)

  const handleZoomWithWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey) return

    const delta = e.deltaY / wheelSlew

    setScale((v) => scaleCutoff(v + delta))
  }

  const handleZoomWithTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    setIsManualScrollActive(true)

    if (e.touches.length !== 2) return

    setScaleTouchStartDistance(Math.abs(e.touches[0].clientX - e.touches[1].clientX))
  }

  const handleZoomWithTouch = (e: TouchEvent<HTMLDivElement>) => {
    if (e.touches.length == 2) {
      setScale((v) => {
        const scaleTouchCurrentDistance = Math.abs(e.touches[0].clientX - e.touches[1].clientX)

        const differentialRaw = (scaleTouchStartDistance! - scaleTouchCurrentDistance) / pinchSlew + 1
        const differential = differentialRaw > 0 ? 1 / differentialRaw : -differentialRaw

        return scaleCutoff(v * differential)
      })
    }
  }

  const renderPlayButton = () => (
    <CircleButton
      className={classNames(styles.playButton, {
        [styles.fullPage]: fullPage,
      })}
      size={fullPage ? 'large' : 'small'}
      attributes={{
        'data-play': id,
      }}
      loading={loading}
      icon={isPlaying ? 'pause' : 'play'}
      onClick={() => {
        if (showPlay) setShowPlay(false)
        if (!playDisabled) handlePlay()
      }}
    />
  )
  const getInstrumentViewSize = () => {
    if (isMobile) {
      return 'small'
    }
    if (isSmallDesktop) {
      return 'medium'
    }
    return 'large'
  }
  const renderEditInButton = () => {
    return (
      <button
        onClick={handleOpenPreviewInStudio}
        className={classNames(styles.editButton, {
          [styles.fullPage]: fullPage,
        })}
      >
        <IconMenuButtonLight icon={fullPage ? <ChordEditIcon /> : <ExternalLinkIcon />} className={styles.icon} />
        {<span>{fullPage ? STRINGS.editInGenerator : STRINGS.editInGeneratorShort}</span>}
      </button>
    )
  }
  const renderBlogPreviewTooltip = (id: string, title: string) => {
    return (
      <Tooltip id={id} className={styles.blogPreviewTooltip} delayShow={0} clickable openOnClick>
        <div className={styles.blogPreviewTooltipContent}>
          <div className={styles.blogPreviewTooltipTitle}>{title}</div>
          <div className={styles.blogPreviewTooltipText}>Customize chords, generate melodies, add drums & more!</div>
          <div className={styles.blogPreviewTooltipBtn} onClick={handleOpenPreviewInStudio}>
            Edit Chords
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(styles.container, {
          [styles.fullPage]: fullPage,
        })}
      >
        <div
          className={classNames(styles.chordsContainer, {
            [styles.fullPage]: fullPage,
          })}
          data-play={fullPage ? id : ''}
          onClick={() => {
            if (!fullPage) return
            setShowPlay(false)
            if (!playDisabled) handlePlay()
          }}
          onWheel={handleZoomWithWheel}
          onTouchStart={handleZoomWithTouchStart}
          onTouchMove={handleZoomWithTouch}
          onTouchEnd={() => setIsManualScrollActive(false)}
        >
          {showPlay ? (
            <div className={styles.playContainer}>
              <div className={styles.play}>
                <PlayIcon />
              </div>
            </div>
          ) : null}
          {fullPage ? <PlayPauseAnimation isPlaying={isPlaying} /> : null}
          <div
            ref={chordsContainerRef}
            className={classNames(styles.chordsContent, {
              [styles.fullPage]: fullPage,
            })}
          >
            {activePart.chords.length > 0 && (
              <div
                className={classNames(
                  styles.chords,
                  { [styles.fullPage]: fullPage },
                  { [styles.chordsMuted]: getIsTrackMuted(activePart, 'CHORDS') },
                )}
                style={{
                  gridTemplateColumns: activePart.chords.map((ch) => `${(ch.duration || 1) * 16 * 16}fr`).join(' '),
                }}
              >
                {activePart.chords.map((ch) => (
                  <div
                    key={ch.id}
                    style={{
                      [fullPage ? 'width' : 'minWidth']: (ch.duration || 1) * CHORD_WIDTH * scale,
                    }}
                    className={classNames(styles.chord, {
                      [styles.fullPage]: fullPage,
                    })}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (isPlaying) {
                        handlePlay()
                      }
                      playChord && playChord(ch)
                    }}
                    data-active={activeChordId === ch.id}
                  >
                    <p>{ch.name}</p>
                    <span>{removeScaleFromDegree(ch.degree)}</span>
                  </div>
                ))}
              </div>
            )}
            {renderDrumsPreview ? renderDrumsPreview(scale) : null}
          </div>
        </div>
        {fullPage ? (
          <div className={classNames(styles.footer, styles.fullPage)}>{renderEditInButton()}</div>
        ) : (
          <div className={styles.footer}>
            <CircleButton className={classNames(styles.backButton, styles.hiddenButton)} size='small' />
            <CircleButton className={styles.backButton} size='small' onClick={handleReset} icon='step-backward' />
            {renderPlayButton()}
            <div className={styles.keyInfo} data-tooltip-id={blogPreviewKeyTooltipId}>
              <p>{prog.key}</p>
              <span>Key</span>
            </div>
            <div className={styles.bpmInfo} data-tooltip-id={blogPreviewBpmTooltipId}>
              <p>{bpm}</p>
              <span>BPM</span>
            </div>

            {renderBlogPreviewTooltip(blogPreviewKeyTooltipId, 'Change The Key')}
            {renderBlogPreviewTooltip(blogPreviewBpmTooltipId, 'Change The Bpm')}
          </div>
        )}
        {!fullPage && renderEditInButton()}
      </div>
      {renderFooter &&
        renderFooter(
          () => (
            <>
              <div className={styles.leftBar}>
                <VolumeControls
                  volume={volume}
                  onVolumeChange={(v) => {
                    handleVolumeChange(v)
                    setLocalVolume(v)
                    if (muted) setMuted(false)
                  }}
                  onlyMute={!!isMobile}
                  muted={muted}
                  setMuted={(v) => {
                    setMuted(v)
                    if (v) {
                      handleVolumeChange(0)
                    } else {
                      handleVolumeChange(localVolume)
                    }
                  }}
                  disableOnMute={false}
                />
              </div>

              <CircleButton
                className={classNames(styles.backButton, styles.fullPage)}
                size='medium'
                onClick={() => {
                  handleReset()
                  chordsContainerRef.current?.scrollTo({ left: 0 })
                }}
                icon='step-backward'
              />
              {renderPlayButton()}
              <CircleButton
                className={classNames(styles.repeatButton, styles.fullPage, {
                  [styles.active]: repeat,
                })}
                size='medium'
                onClick={handleRepeat}
                icon='repeat'
              />
              <div className={styles.rightBar}>
                <div className={styles.bpmInfo}>
                  <p>{bpm}</p>
                  <span>BPM</span>
                </div>
                <div className={styles.keyInfo}>
                  <p>{prog.key}</p>
                  <span>Key</span>
                </div>
              </div>
            </>
          ),
          () => (
            <PianoRoll
              fromOctave={0}
              octaves={7}
              size={getInstrumentViewSize()}
              playingMidi={
                !!activeChord && isPlaying
                  ? (activeChord?.midi || []).map((midi) => midi + (activeChord?.octave || 0) * 12)
                  : []
              }
              setPianoScrollDisabled={() => {}}
            />
          ),
        )}
    </>
  )
}

export default Preview
